import * as React from "react";
import { couponRegex, interpolate } from "../../utils/string";
import get from "lodash/get";
import ReactMarkdown from "react-markdown";
import gfm from "remark-gfm";

import "./Text.css";
// import { sendDragToMerchant } from "../../utils/sendDragToMerchant";
import { ComponentBase, ComponentBaseProps } from "../ComponentBase";
import { getCoupon } from "../../reducers/methods";
import { extractInfluencerAndAdvocate } from "../../utils/share";

import { openNewWindowActions } from "../../constants";

export interface TextProps extends ComponentBaseProps {
  onClick: () => void;
  action: string;
  successScreen: string;
  mode: string;
  content: string;
  submitButton?: boolean;
  formErrors: {
    [key: string]: { [index: string]: any };
  };
  copyText?: string;
  couponObject?: ReturnType<typeof getCoupon>;
}

export class Text extends ComponentBase<TextProps> {
  // tslint:disable:cyclomatic-complexity
  render() {
    const {
      style,
      classNames,
      data,
      referral,
      rewardStats,
      share,
      action,
      successScreen,
      mode,
      submitButton = false,
      isEditing,
      copyText,
      config,
      couponObject,
    } = this.props;

    // Support both old (nested) and new (direct) share data structures
    // Old: config.data.share.share (merchant SDK bug)
    // New: config.data.share (correct structure)
    const shareDataRaw = get(config, ["data", "share"], {});
    const configShareData =
      shareDataRaw.firstName || shareDataRaw.lastName || shareDataRaw.name
        ? shareDataRaw // New structure: data is directly in share
        : get(shareDataRaw, ["share"], {}); // Old structure: data is nested in share.share

    const { advocate, influencer } = extractInfluencerAndAdvocate(
      configShareData,
      referral
    );

    const { tempTextReplace } = this.state;
    // If the action URL contains a coupon, we need to wait for the coupon to load
    const actionURL = get(action, "args.url", "");
    const containsCoupon = couponRegex.test(actionURL);
    const waitingForCoupon =
      containsCoupon && get(couponObject, "status") === "pending";

    // const defaultToBlank = !isEditMode();
    const interpolatedString = waitingForCoupon
      ? "Loading"
      : interpolate(
          tempTextReplace
            ? (tempTextReplace as unknown as string)
            : this.props.content,
          {
            ...data,
            referral,
            share,
            rewardStats,
            advocate,
            influencer,
          }
        );

    const actionName = get(action, "name", "");
    const isNewWindowAction = openNewWindowActions.includes(actionName);
    const isCopyAction = actionName === "copyText";

    const actionLabel = `${get(this.props, "label", "")} ${
      isNewWindowAction ? " (opens in a new window)" : ""
    }`;

    const actionRole = isNewWindowAction ? "link" : "button";

    const cursorStyle = waitingForCoupon
      ? "wait"
      : action || successScreen
        ? "pointer"
        : "inherit";

    if (action && submitButton) {
      return (
        <button
          id={this.props.configName}
          onDragStart={this.onDragStart}
          // onMouseDown={this.onMouseDown}
          // onClick={this.onClick}
          onAnimationEnd={this.resetAnimation}
          style={{
            outline: "inherit",
            background: "none",
            border: "none",
            ...style,
            cursor: cursorStyle,
          }}
          className={`Text-text Text-mode-${mode || "default"} ${
            classNames || ""
          } ${!isEditing ? "Text-text-preview" : ""} ${
            this.state.animationClass
          }`}
          type="submit"
          aria-label={
            isCopyAction ? `Click to copy ${copyText}` : `${actionLabel}`
          }
          tabIndex={0}
          ref={(component) => (this.component = component)}
        >
          <span className="Text-markdown-container">
            <ReactMarkdown skipHtml={true} linkTarget="_blank" plugins={[gfm]}>
              {interpolatedString}
            </ReactMarkdown>
          </span>
        </button>
      );
    }

    return (
      <div
        onDragStart={this.onDragStart}
        id={this.props.configName}
        onAnimationEnd={this.resetAnimation}
        style={{
          ...style,
          cursor: cursorStyle,
        }}
        className={`Text-text  Text-mode-${mode || "default"} ${
          classNames || ""
        } ${!isEditing ? "Text-text-preview" : ""} ${
          this.state.animationClass
        }`}
        {...(action ? { "aria-label": actionLabel } : {})}
        {...(isCopyAction ? { "aria-live": "polite" } : {})}
        tabIndex={action ? 0 : undefined}
        ref={(component) => (this.component = component)}
        role={action ? actionRole : undefined}
      >
        <span
          className={`Text-markdown-container ${isCopyAction && "copyText"}`}
        >
          {isCopyAction ? (
            interpolatedString
          ) : (
            <ReactMarkdown
              skipHtml={true}
              linkTarget="_blank"
              renderers={{
                link: MarkdownLink,
              }}
              plugins={[gfm]}
            >
              {interpolatedString}
            </ReactMarkdown>
          )}
        </span>
      </div>
    );
  }
}

interface MarkdownLinkProps {
  target: string;
  href: string;
}

const MarkdownLink: React.FC<MarkdownLinkProps> = ({
  target,
  href,
  children,
}) => {
  const newWindowLabel = target === "_blank" ? " (opens in a new window)" : "";

  return (
    <a
      style={{
        color: "inherit",
        textDecoration: "inherit",
      }}
      target={target}
      href={href}
    >
      {children}
      <span className="screen-reader-only">{newWindowLabel}</span>
    </a>
  );
};
